import React, { memo, useState, useEffect } from 'react';
import classname from 'classnames';
import { Modal } from 'zarm';
import { action } from '@iyunbao/cr-engine';
import { Icon } from '@iyunbao/components';
import Button from '../form/button';
import fetchAPI from '../utils/fetchAPI';
import LockScroll from '../lockScroll';

export interface ModalLoadingProps {
  title?: string;
  description?: string;
  childrens: any[];
  _crengine: any;
  visible: boolean;
}

export interface SetChildrenParams {
  children: any[];
  status: 'before' | 'fetching' | 'success' | 'waiting' | 'fail' | 'refetch';
  indexCurrent: number;
  reason?: string;
  waiting?: string;
  itemAction?: { [key: string]: any };
  initFetch?:{ [key: string]: any };
}

// 函数延迟的 time 毫秒执行
const sleep = async (time: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, time);
  });
};

const UModalLoading = (props: ModalLoadingProps) => {
  const { title, description, childrens, _crengine = {}, visible: initVisible = false } = props;

  // 初始化时没有status时设置为before，
  const formatChildren = childrens.map((item) => {
    if (!item.status) {
      item.status = 'before';
    }
    return item;
  });

  // 请求接口的列表children
  const [newChildren, setChildren] = useState(formatChildren);

  // 弹框是否关闭，初始化开启，请求结束或者点击按钮关闭
  const [visible, setVisible] = useState(initVisible);

  // 当前请求接口第几项的下标
  const [fetchIndex, setFetchIndex] = useState(0);

  // 关闭弹框 并 跳转按钮
  const handleSubmit = async (lastAction) => {
    setVisible(false);
    // 单页应用时弹框无法自动清除dom，需要手动关闭弹框后再跳转
    await sleep(10);
    if (!lastAction) {
      return;
    }
    const { action: waction } = _crengine;
    if (lastAction) {
      waction(lastAction);
      return;
    }

    const { type, data } = lastAction;
    if (action[type]) {
      action[type](data);
    }
  };

  /**
   *
   * @param children 接口请求的数组
   * @param status  当前接口请求的状态
   * @param indexCurrent 数组中当前请求的下标
   * @param reason 任意一步，请求错误的原因展示
   * @param waiting 当最后一个请求，需要人工核保，请等待时的提示文案
   * @param itemAction 最后一步的按钮当前接口返回值的行为，比如跳转新页面
   */
  const setChildrenStatus = ({ children, status, indexCurrent, reason, waiting, itemAction, initFetch }: SetChildrenParams) => {
    const mapChildStatus = children.map((item, index) => {
      if (index === indexCurrent) {
        item.status = status;
        if (waiting) {
          item.waiting = waiting;
        }
        if (reason) {
          item.reason = reason;
        }
        if (itemAction) {
          item.itemAction = itemAction;
        }
        if (initFetch) {
          item.initFetch = initFetch;
        }
      }
      return item;
    });
    setChildren(mapChildStatus);
  };
  
  // 触发请求
  const triggerFetch = async () => {
    // await sleep(500);
    const item = newChildren[fetchIndex];
    const length = newChildren.length - 1;
    setChildrenStatus({ children: newChildren, status: 'fetching', indexCurrent: fetchIndex });
    fetchAPI.fetchResult(item.initFetch).then(
      async (content: any) => {
        const { result } = content || {};
        // await sleep(500);
        if (!result) {
          setChildrenStatus({
            children: newChildren,
            status: 'fail',
            indexCurrent: fetchIndex,
          });
          return;
        }

        // 开发用，指定某个接口返回值
        // if (fetchIndex === 2) {
        //   result.code = 2;
        //   result.message = "message";
        //   result.reason = "reason";
        //   result.action = {
        //     type: "url",
        //     data: {
        //       url: "https://www.iyunbao.com"
        //     }
        //   };
        // }
        
        // let _result = {
        //   code: '3',
        //   message:'message111',
        //   reason:'reason111',
        //   action: {}
        // };
        const resCode = String(result.code);

        const { message: waiting, reason, action: itemAction } = result;
        // 0成功，其它情况失败
        if (resCode === '0') {
          setChildrenStatus({
            children: newChildren,
            status: 'success',
            indexCurrent: fetchIndex,
            reason,
          });
          // 非最后一个，加1。最后一个等1秒触发跳转事件
          if (fetchIndex < length) {
            setFetchIndex(fetchIndex + 1);
          } else {
            await sleep(1000);
            handleSubmit(itemAction);
          }
        } else if (resCode === '1') {
          // 1中间态，需要人工核保，
          setChildrenStatus({
            children: newChildren,
            status: 'waiting',
            indexCurrent: fetchIndex,
            waiting,
            reason,
            itemAction,
          });
        } else if (resCode === '3') {
          // 3 为重复调用
          let newInitFetch = item.initFetch || {};
          if (newInitFetch.data) {
            newInitFetch.data._refetch = true;
          }
          // console.log('newInitFetch===', newInitFetch);
          setChildrenStatus({
            children: newChildren,
            status: 'refetch',
            indexCurrent: fetchIndex,
            waiting,
            reason,
            itemAction,
            initFetch: newInitFetch,
          });

          triggerFetch();
        } else {
          setChildrenStatus({
            children: newChildren,
            status: 'fail',
            indexCurrent: fetchIndex,
            reason,
          });
        }
      },
      (error: string) => {
        if (error && typeof error === 'string') {
          setChildrenStatus({
            children: newChildren,
            status: 'fail',
            indexCurrent: fetchIndex,
            reason: error,
          });
        }
      },
    );
  };

  useEffect(
    () => {
      triggerFetch();
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchIndex],
  );

  const iconMap = {
    fetching: <span className="modal-loading-fetching__icon" />,
    success: <Icon type="xuanzhong-shixin" style={{ color: '#057AFF', marginRight: 3 }} />,
    // fail: <Icon type="tupianjiazaishibai" style={{ color: "##FF5050", marginRight: 3 }} />,
    fail: <span className="modal-loading-fail__icon" />,
    waiting: <Icon type="shibai" style={{ color: '#FF6E0D', marginRight: 3 }} />,
  };

  // 渲染按钮和错误提示文案
  const renderBtn = () => {
    const currentItem = newChildren[fetchIndex];
    const currentStatus = currentItem.status;
    const isLast = fetchIndex === newChildren.length - 1;

    if (isLast) {
      // 最后一步，失败了，提示我知道了
      if (currentStatus === 'fail') {
        return (
          <div>
            {!!currentItem.reason && <div className="modal-loading-reason">{currentItem.reason}</div>}
            <Button
              shape="round"
              theme="primary"
              className="modal-loading-btn"
              onSubmit={() => setVisible(false)}
              label="我知道了"
            />
          </div>
        );
      }
      // 最后一步，成功了，但是需要人工核保
      if (currentStatus === 'waiting') {
        return (
          <div>
            {!!currentItem.reason && <div className="modal-loading-reason">{currentItem.reason}</div>}
            <Button
              shape="round"
              theme="primary"
              className="modal-loading-btn"
              onSubmit={() => handleSubmit(currentItem.itemAction)}
              label="确认"
            />
          </div>
        );
      }
    } else if (currentStatus === 'fail') {
      // 不是最后一步，失败了重试
      return (
        <div>
          {!!currentItem.reason && <div className="modal-loading-reason">{currentItem.reason}</div>}
          <Button shape="round" theme="primary" className="modal-loading-btn" onSubmit={triggerFetch} label="重试" />
        </div>
      );
    }
  };

  return (
    <LockScroll>
      <Modal visible={visible}>
        <div className="modal-loading">
          {!!title && <div className="modal-loading-title">{title}</div>}
          {!!description && <div className="modal-loading-desc">{description}</div>}
          {(newChildren || []).map((item) => {
          const { before, status: itemStatus } = item;
          const clsItem = classname('modal-loading-item', {
            'modal-loading-before': itemStatus === 'before',
            'modal-loading-success': itemStatus === 'success',
            'modal-loading-fail': itemStatus === 'fail',
            'modal-loading-waiting': itemStatus === 'waiting',
          });
          const iconItem = iconMap[itemStatus];
          return (
            <div key={before} className={clsItem}>
              {iconItem}
              {item[itemStatus]}
            </div>
          );
        })}
          {renderBtn()}
        </div>
      </Modal>
    </LockScroll>
  );
};

export default memo(UModalLoading);
